import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Paper, TextField, Button, Typography, Box, IconButton, Tooltip, FormHelperText, Divider, Grid, Chip } from '@material-ui/core';
import { Settings } from '@material-ui/icons';
import MaterialTable from 'material-table';
import { localization } from '../../config';
import { fetchContenants, fetchPesees, createPesee, updatePesee, deletePesee } from '../../actions';
import { Link } from 'react-router-dom';

// Styles basés sur votre modèle
const usePageStyles = makeStyles(theme => ({
  wrap: { maxWidth: 1700, margin: '0 auto', padding: theme.spacing(0, 3, 6) },
  formAndListLayout: { display: 'grid', gridTemplateColumns: '520px minmax(760px,1fr)', gap: theme.spacing(5) },

  formCard: {
    padding: theme.spacing(3),
    borderRadius: 20,
    background: 'linear-gradient(135deg,#ffffff 0%, #fafbff 100%)',
    boxShadow: '0 10px 28px -4px rgba(32,40,52,0.10), 0 4px 12px -2px rgba(32,40,52,0.06)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 520,
    position: 'relative'
  },

  listColumnWrap: { display: 'flex', flexDirection: 'column', minHeight: 480, overflow: 'hidden', boxShadow: '0 10px 28px -4px rgba(32,40,52,0.10), 0 4px 12px -2px rgba(32,40,52,0.06)', borderRadius: 20, background: '#fff' },
  tableContainer: { overflow: 'hidden', borderRadius: 20, display: 'flex', flexDirection: 'column', height: '100%', boxShadow: 'none' },
  listTitleWrap: { padding: theme.spacing(3, 3, 0) },
  listTitle: {
    fontWeight: 600,
    letterSpacing: '-0.5px',
    fontSize: 32,
    background: 'linear-gradient(90deg,#1d1f27 0%, #2f3b55 100%)',
    WebkitBackgroundClip: 'text',
    color: 'transparent'
  },

  field: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: '#fff',
      transition: 'box-shadow .25s, border-color .25s',
      '& fieldset': { borderColor: '#dfe3ea' },
      '&:hover fieldset': { borderColor: '#b8c2cf' },
      '&.Mui-focused': {
        boxShadow: '0 0 0 3px rgba(66,133,244,0.25)',
        '& fieldset': { borderColor: '#4285f4' }
      }
    },
    '& .MuiInputLabel-outlined.Mui-focused': { color: '#1d60d6' },
  },

  headerRow: { display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: theme.spacing(2), alignItems: 'center' },
  cardsScroll: { marginTop: theme.spacing(2), flex: 1, overflowY: 'auto', paddingRight: 4 },
  card: {
    padding: theme.spacing(2),
    borderRadius: 12,
    boxShadow: '0 2px 8px -2px rgba(20,30,55,0.08)',
    display: 'grid',
    gridTemplateColumns: '1fr 120px 120px',
    gap: theme.spacing(2),
    alignItems: 'center',
    background: '#fff'
  },
  cardTitle: { fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  badgeTare: { background: 'rgba(26,115,232,0.08)', color: '#1a73e8', marginLeft: theme.spacing(1) },
  totalBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  actionsRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(3),
    justifyContent: 'flex-end'
  },
  primaryBtn: {
    borderRadius: 14,
    padding: '10px 20px',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '.3px',
    background: 'linear-gradient(90deg,#1a73e8 0%,#1967d2 100%)',
    boxShadow: '0 4px 14px -2px rgba(26,115,232,0.35)',
    '&:hover': { background: 'linear-gradient(90deg,#1b6fdc 0%,#155fc2 100%)' }
  },
  resetBtn: { borderRadius: 14, textTransform: 'none', fontWeight: 500 },
}));

const round2 = v => Number((Math.round((v + Number.EPSILON) * 100) / 100).toFixed(2));

// Composant pour chaque ligne de pesée (Contenant)
const PeseeCard = React.memo(({ contenant, rowData, setBrutFor, classes }) => {
  const rawValue = rowData.brut != null ? String(rowData.brut).replace('.', ',') : '';

  // Fonction de normalisation numérique simple pour l'affichage dans le champ
  const formatNumericInput = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return '';
    let v = String(raw).replace(/[\s\u00A0\u202F]+/g, '');
    v = v.replace(/[^0-9.,]/g, '');
    const parts = v.split(/[.,]/);
    let int = parts[0] || '';
    let dec = parts[1] || '';
    if (int.length > 4) int = int.slice(0, 4); // maxInt 4 par défaut
    if (dec.length > 2) dec = dec.slice(0, 2);
    if (v.includes('.') || v.includes(',')) return dec.length ? `${int}${v.includes('.') ? '.' : ','}${dec}` : `${int}${v.includes('.') ? '.' : ','}`;
    return int;
  };

  const displayValue = formatNumericInput(rawValue);

  return (
    <Grid item xs={12} key={contenant.id}>
      <div className={classes.card}>
        <div>
          <div className={classes.cardTitle} title={contenant.titre}>
            {contenant.titre}
            <Chip size="small" label={`Tare ${contenant.tare != null ? Number(contenant.tare).toFixed(2) : '0.00'} g`} className={classes.badgeTare} />
          </div>
        </div>
        <TextField
          label="Brut (g)"
          variant="outlined"
          size="small"
          type="text" // Changé en text pour mieux gérer la virgule et la fonction onChange
          inputMode="decimal"
          inputProps={{ step: '0.01' }}
          value={displayValue}
          onChange={e => setBrutFor(contenant.id, e.target.value)}
          className={classes.field}
          onFocus={e => { e.target.select && e.target.select(); }}
        />
        <TextField
          className={classes.field}
          label="Net (g)"
          variant="outlined"
          size="small"
          value={rowData.net != null ? Number(rowData.net).toFixed(2) : ''}
          disabled
        />
      </div>
    </Grid>
  );
});


const PeseePage = () => {
  const classes = usePageStyles();
  const dispatch = useDispatch();
  const contenants = useSelector(s => s.contenants.list) || [];
  const pesees = useSelector(s => s.pesees.list) || [];

  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [titre, setTitre] = useState('');
  const [lignes, setLignes] = useState([]); // { id_contenant, brut, net }
  const [edit, setEdit] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dispatch(fetchContenants()),
      dispatch(fetchPesees())
    ]).finally(() => setLoading(false));
  }, [dispatch]);

  const contenantsMap = useMemo(() => new Map(contenants.map(c => [c.id, c])), [contenants]);
  const actifs = useMemo(() => contenants.filter(c => c.actif), [contenants]);
  const totalNet = useMemo(() => round2((lignes || []).reduce((s, l) => s + (Number(l.net) || 0), 0)), [lignes]);

  // Réinitialisation/Chargement des données d'édition
  useEffect(() => {
    if (edit) {
      setDate(edit.date || new Date().toISOString().slice(0, 10));
      setTitre(edit.titre || '');
      const ls = (edit.lignes || []).map(l => ({
        id_contenant: l.id_contenant,
        // Stocker la valeur brute dans le format original pour le calcul/affichage
        brut: Number(l.poids_brut),
        net: Number(l.poids_net)
      }));
      setLignes(ls);
    } else {
      setDate(new Date().toISOString().slice(0, 10)); setTitre(''); setLignes([]);
    }
  }, [edit]);

  const setBrutFor = useCallback((id_contenant, value) => {
    const rawValue = String(value).trim();
    const contenant = contenantsMap.get(id_contenant);
    const tare = Number(contenant?.tare || 0);

    let brut;
    if (rawValue === '') {
      brut = '';
    } else {
      const parsed = parseFloat(rawValue.replace(',', '.'));
      brut = isNaN(parsed) || parsed < 0 ? '' : parsed;
    }

    const net = brut === '' ? '' : round2(Math.max(0, brut - tare));

    setLignes(prev => {
      const idx = prev.findIndex(x => x.id_contenant === id_contenant);
      const next = [...prev];
      const row = { id_contenant, brut, net };

      // Si brut est vide ou invalide, on retire la ligne
      if (brut === '') {
        return next.filter(r => r.id_contenant !== id_contenant);
      }

      if (idx === -1) {
        next.push(row);
      } else {
        next[idx] = row;
      }
      return next.filter(r => r.brut !== '' && !isNaN(r.brut));
    });
  }, [contenantsMap]);


  const onSubmit = async (e) => {
    e.preventDefault();

    const lignesClean = (lignes || [])
      .filter(l => l.brut !== '' && !isNaN(l.brut) && Number(l.net) > 0)
      .map(l => ({
        id_contenant: l.id_contenant,
        poids_brut: round2(l.brut),
        poids_net: round2(Math.max(0, Number(l.net) || 0))
      }));

    const calculatedTotalNet = round2(lignesClean.reduce((s, l) => s + (l.poids_net || 0), 0));

    const payload = {
      date,
      titre: titre?.trim(),
      lignes: lignesClean,
      total_net: calculatedTotalNet
    };

    if (!payload.titre) return alert('Le titre est requis.');
    if (!payload.lignes.length) return alert('Veuillez saisir au moins une ligne de pesée valide (net > 0).');

    try {
      if (edit?.id) {
        await dispatch(updatePesee(edit.id, payload));
      } else {
        await dispatch(createPesee(payload));
      }
      setEdit(null);
    } catch (error) {
      alert("Une erreur est survenue lors de l'opération.");
      console.error(error);
    }
  };

  const columns = useMemo(() => ([
    { title: 'Date', field: 'date', defaultSort: 'desc' },
    { title: 'Titre', field: 'titre' },
    {
      title: 'Solde (g)',
      field: 'total_net',
      render: r => r.total_net != null ? Number(r.total_net).toFixed(2) : '',
      type: 'numeric'
    },
  ]), []);

  return (
    <div className={classes.wrap}>
      <div className={classes.formAndListLayout}>
        {/* === Form Card (Saisie) === */}
        <Paper className={classes.formCard}>
          <Box className={classes.headerRow}>
            <TextField
              label="Date"
              name="date"
              type="date"
              variant="outlined"
              size="small"
              value={date}
              onChange={e => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              className={classes.field}
            />
            <TextField
              label="Désignation de la pesée"
              variant="outlined"
              size="small"
              value={titre}
              onChange={e => setTitre(e.target.value)}
              required
              className={classes.field}
            />
            <Tooltip title="Gérer les contenants">
              <IconButton component={Link} to="/atelier/contenants"><Settings /></IconButton>
            </Tooltip>
          </Box>
          <Divider style={{ margin: '20px 0' }} />
          <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 8, color: '#1d1f27' }}>Saisie des Poids Bruts</Typography>
          <div className={classes.cardsScroll}>
            {actifs.length === 0 && <FormHelperText>Pas de contenant actif. Cliquez sur la roue dentée pour en créer.</FormHelperText>}
            <Grid container spacing={2}>
              {actifs.map(c => {
                const row = lignes.find(l => l.id_contenant === c.id) || {};
                return (
                  <PeseeCard
                    key={c.id}
                    contenant={c}
                    rowData={row}
                    setBrutFor={setBrutFor}
                    classes={classes}
                  />
                );
              })}
            </Grid>
          </div>
          <div className={classes.totalBar}>
            <Box display="flex" alignItems="center" fontSize={18} fontWeight={700}>
              Total Net: <Box ml={1} color="#1b7f36">{totalNet.toFixed(2)} g</Box>
            </Box>
          </div>
          <div className={classes.actionsRow}>
            {edit && <Button className={classes.resetBtn} variant="outlined" onClick={() => setEdit(null)} size="large">Annuler</Button>}
            <Button onClick={onSubmit} className={classes.primaryBtn} variant="contained" size="large">{edit ? 'Mettre à jour la pesée' : 'Enregistrer la pesée'}</Button>
          </div>
        </Paper>

        {/* === List Column (Historique) === */}
        <div className={classes.listColumnWrap}>
          <div className={classes.listTitleWrap}><Typography className={classes.listTitle}>Historique des pesées</Typography></div>
          <div className={classes.tableContainer}>
            <MaterialTable
              isLoading={loading}
              localization={localization}
              columns={columns}
              data={pesees}
              options={{ paging: true, actionsColumnIndex: -1, headerStyle: { fontWeight: 'bold' } }}
              actions={[
                { icon: 'edit', tooltip: 'Modifier', onClick: (e, row) => setEdit(row) },
                { icon: 'delete', tooltip: 'Supprimer', onClick: async (e, row) => { if (window.confirm('Supprimer cette pesée ?')) await dispatch(deletePesee(row.id)); } }
              ]}
              detailPanel={rowData => {
                const lignes = rowData.lignes || [];
                if (!lignes.length) return <div style={{ padding: 16 }}>Aucune ligne</div>;
                return (
                  <div style={{ padding: 16 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                      <thead style={{ borderBottom: '2px solid #eee' }}>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '8px 0' }}>Contenant</th>
                          <th style={{ textAlign: 'right', padding: '8px 0' }}>Brut (g)</th>
                          <th style={{ textAlign: 'right', padding: '8px 0' }}>Tare (g)</th>
                          <th style={{ textAlign: 'right', padding: '8px 0' }}>Net (g)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lignes.map((l, idx) => {
                          const c = contenantsMap.get(l.id_contenant);
                          return (
                            <tr key={idx} style={{ borderBottom: '1px solid #f7f7f7' }}>
                              <td style={{ textAlign: 'left', padding: '8px 0', fontSize: 14 }}>{c ? c.titre : `#${l.id_contenant}`}</td>
                              <td style={{ textAlign: 'right', padding: '8px 0', fontSize: 14 }}>{Number(l.poids_brut).toFixed(2)}</td>
                              <td style={{ textAlign: 'right', padding: '8px 0', fontSize: 14 }}>{c ? Number(c.tare).toFixed(2) : '—'}</td>
                              <td style={{ textAlign: 'right', padding: '8px 0', fontWeight: 600, color: '#1b7f36', fontSize: 14 }}>{Number(l.poids_net).toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeseePage;